'use client';

import { useState } from 'react';
import Image, { type StaticImageData } from 'next/image';
import Link from 'next/link';
import RiArrowLeftSLine from 'remixicon-react/ArrowLeftSLineIcon';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import ProductCardV2, {
  type ProductCardV2Props,
} from '@/common/components/product-card-v2/product-card-v2';
import 'swiper/css';

type CarouselProduct = ProductCardV2Props & {
  id: string | number;
};

interface ProductCarouselProps {
  title: string;
  icon: string | StaticImageData;
  iconAlt?: string;
  backgroundColor: string;
  products: CarouselProduct[];
  viewAllHref: string;
  viewAllLabel?: string;
  className?: string;
}

function ProductCarousel({
  title,
  icon,
  iconAlt = '',
  backgroundColor,
  products,
  viewAllHref,
  viewAllLabel = 'مشاهده همه',
  className = '',
}: ProductCarouselProps) {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [isEnd, setIsEnd] = useState(false);

  const updateNavigationState = (instance: SwiperType) => {
    setIsEnd(instance.isEnd);
  };

  return (
    <section
      dir="rtl"
      aria-label={title}
      style={{ backgroundColor }}
      className={`
        w-full min-w-0 overflow-hidden
        py-4
        md:flex md:items-center md:gap-8
        md:rounded-2xl md:py-6 md:pr-6 md:pl-0
        ${className}
      `}
    >
      <div className="mb-4 flex items-center justify-between px-4 md:mb-0 md:hidden md:px-0">
        <div className="flex min-w-0 items-center gap-1">
          <h2 className="truncate text-base font-bold leading-6 text-white">
            {title}
          </h2>

          <div className="relative h-7 w-[30px] shrink-0">
            <Image
              src={icon}
              alt={iconAlt}
              fill
              sizes="30px"
              loading="lazy"
              className="object-contain"
            />
          </div>
        </div>

        <Link
          href={viewAllHref}
          className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-white"
        >
          <span>{viewAllLabel}</span>
          <RiArrowLeftSLine className="size-4" />
        </Link>
      </div>

      <aside className="hidden w-[120px] shrink-0 flex-col items-center justify-center gap-6 text-white md:flex">
        <h2 className="text-center text-2xl font-bold leading-8">{title}</h2>

        <div className="relative h-[114px] w-[120px]">
          <Image
            src={icon}
            alt={iconAlt}
            fill
            sizes="120px"
            loading="lazy"
            className="object-contain"
          />
        </div>

        <Link
          href={viewAllHref}
          className="flex items-center justify-center text-sm font-semibold leading-5 text-white transition-opacity hover:opacity-80"
        >
          <span>{viewAllLabel}</span>
          <RiArrowLeftSLine className="size-6" />
        </Link>
      </aside>

      <div className="relative min-w-0 flex-1 overflow-hidden pr-4 md:pr-0">
        <Swiper
          dir="rtl"
          slidesPerView={2.35}
          spaceBetween={8}
          breakpoints={{
            768: {
              slidesPerView: 'auto',
              spaceBetween: 12,
            },
          }}
          grabCursor
          watchOverflow
          onSwiper={(instance) => {
            setSwiper(instance);
            updateNavigationState(instance);
          }}
          onSlideChange={updateNavigationState}
          onResize={updateNavigationState}
          className="!w-full !min-w-0 !overflow-hidden"
        >
          {products.map(({ id, ...product }) => (
            <SwiperSlide
              key={id}
              className="
                !h-auto
                md:!w-[180px]
              "
            >
              <ProductCardV2 {...product} />
            </SwiperSlide>
          ))}
        </Swiper>

        {!isEnd && (
          <button
            type="button"
            onClick={() => swiper?.slideNext()}
            aria-label="مشاهده محصولات بیشتر"
            className="
              absolute left-2 top-1/2 z-20
              hidden size-10 -translate-y-1/2
              items-center justify-center
              rounded-full border border-silver-default bg-white
              text-black-400
              shadow-[0_2px_12.6px_rgba(157,157,157,0.2)]
              transition-transform
              hover:scale-105
              active:scale-95
              md:flex
            "
          >
            <RiArrowLeftSLine className="size-6" />
          </button>
        )}
      </div>
    </section>
  );
}

export default ProductCarousel;
